import React from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, Text} from '@forecast-it/design-system';
import {formatCreditResetDate, useAiCreditUsage} from '../../../components/gen-ai/aiCreditUsage';
import {useAiCreditsSuppressed} from '../../../components/gen-ai/AiCreditBanner';

/**
 * The account's AI credit usage for the current period. Renders nothing when the balance is
 * unknown or could not be fetched, or while AI is switched off for the account.
 */
const AiCreditsUsage = (): React.ReactElement | null => {
	const {formatMessage, formatNumber} = useIntl();
	const suppressed = useAiCreditsSuppressed();
	const usage = useAiCreditUsage(!suppressed);
	if (!usage) return null;

	const date = formatCreditResetDate(usage.periodEnd);
	if (!date) return null;

	return (
		<fieldset className="ai-credits" data-cy="ai-credits-usage">
			<h3>{formatMessage({id: 'gen_ai.credits.usage.title'})}</h3>
			<p className="description">{formatMessage({id: 'gen_ai.credits.usage.description'})}</p>
			<FlexColumn gap="s">
				<Text data-cy="ai-credits-usage-amount">
					{formatMessage(
						{id: 'gen_ai.credits.usage.amount'},
						{
							used: formatNumber(Math.floor(usage.usedCredits)),
							granted: formatNumber(Math.floor(usage.grantedCredits)),
							percent: formatNumber(usage.percentUsed),
						}
					)}
				</Text>
				<Text color="auxiliary" data-cy="ai-credits-usage-resets">
					{formatMessage({id: 'gen_ai.credits.usage.resets'}, {date})}
				</Text>
			</FlexColumn>
		</fieldset>
	);
};

export default AiCreditsUsage;
